import React from 'react';
import { ActionIcon, Box, Group, Tooltip } from '@mantine/core';
import { IconLayoutSidebar } from '@tabler/icons-react';
import { AppBrandBlock } from './AppBrandBlock';
import { HeaderProjectSwitcher } from './HeaderProjectSwitcher';
import { HeaderUserAvatar } from './HeaderUserAvatar';
import { RequestTrackerToggle } from './RequestTrackerToggle';
import { FeatureTipsHeaderButton } from '../../features/feature-tips';

export interface AppHeaderProps {
  sidebarCollapsed: boolean;
  navbarWidth: number;
  isMobile: boolean;
  onSidebarToggle: () => void;
  mobileNavOpened: boolean;
  /** True while auto-collapse hover peek is showing the full brand column. */
  brandPeek?: boolean;
  onBrandMouseEnter?: () => void;
  onBrandMouseLeave?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  sidebarCollapsed,
  navbarWidth,
  isMobile,
  onSidebarToggle,
  mobileNavOpened,
  brandPeek = false,
  onBrandMouseEnter,
  onBrandMouseLeave,
}) => {
  const headerActions = (
    <Box className="app-header__actions">
      <FeatureTipsHeaderButton />
      <RequestTrackerToggle />
      <HeaderUserAvatar />
    </Box>
  );

  if (isMobile) {
    return (
      <Box component="header" className="app-header app-header--mobile">
        <Box className="app-header__start">
          <Tooltip label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="md"
              onClick={onSidebarToggle}
              aria-label={mobileNavOpened ? 'Close navigation' : 'Toggle navigation'}
            >
              <IconLayoutSidebar size={20} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Box style={{ minWidth: 0, flex: 1 }}>
            <HeaderProjectSwitcher />
          </Box>
        </Box>
        {headerActions}
      </Box>
    );
  }

  return (
    <Box component="header" className="app-header app-header--desktop">
      <Group wrap="nowrap" gap={0} style={{ width: '100%', height: '100%' }}>
        <Box
          className="app-header__brand-column"
          w={navbarWidth}
          px="md"
          onMouseEnter={onBrandMouseEnter}
          onMouseLeave={onBrandMouseLeave}
          style={{
            flexShrink: 0,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            borderRight: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-body)',
            zIndex: brandPeek ? 2 : undefined,
            transition: 'width 140ms ease',
          }}
        >
          <AppBrandBlock collapsed={sidebarCollapsed} />
        </Box>
        <Group
          flex={1}
          h={60}
          px="md"
          justify="space-between"
          wrap="nowrap"
          gap="md"
          style={{ minWidth: 0 }}
        >
          <Box className="app-header__project" style={{ minWidth: 0 }}>
            <HeaderProjectSwitcher />
          </Box>
          {headerActions}
        </Group>
      </Group>
    </Box>
  );
};

export default AppHeader;
